import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, KeyRound, XCircle, Check } from "lucide-react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import ErrorMessage from "../common/ErrorMessage";
import SuccessMessage from "../common/SuccessMessage";
import { isValidPassword } from "../../helpers/Users";
import ModalScreen from "../common/ModalScreen";

interface ResetPasswordProps {
  user: User;
  onResetPassword: (user: User) => void;
}
const ResetPassword: React.FC<ResetPasswordProps> = ({
  user,
  onResetPassword,
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleResetClick = () => {
    if (newPassword !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }
    if (!isValidPassword(newPassword)) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmReset = () => {
    setShowConfirmModal(false);
    setLoading(true);
    api
      .put(API_URLS.USERS + `${user.id}/_actions/reset_password`, {
        new_password: newPassword,
      })
      .then((response) => {
        if (response.status === 200) {
          onResetPassword(user);
          setNewPassword("");
          setConfirmPassword("");
          setError("");
        } else {
          setError("Erro ao redefinir a senha.");
        }
      })
      .catch((error) => {
        console.warn("Erro ao redefinir a senha:", error);
        setError("Erro ao redefinir a senha.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <div className="reset-password-modal container">
        <div className="field">
          <label className="label">Nova Senha</label>
          <div className="control">
            <input
              type="password"
              className={`input ${!isValidPassword(newPassword) ? "is-danger" : ""}`}
              value={newPassword}
              placeholder="Digite a nova senha"
              onFocus={() => setError("")}
              disabled={loading}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          {!isValidPassword(newPassword) && (
            <p className="help is-danger">
              A senha deve ter pelo menos 6 caracteres.
            </p>
          )}
        </div>
        <div className="field">
          <label className="label">Confirmar Senha</label>
          <div className="control">
            <input
              type="password"
              className={`input ${confirmPassword !== newPassword ? "is-danger" : ""}`}
              value={confirmPassword}
              placeholder="Confirme a nova senha"
              onFocus={() => setError("")}
              disabled={loading}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {confirmPassword !== newPassword && (
            <p className="help is-danger">As senhas não coincidem.</p>
          )}
        </div>
        {error && <p className="error has-text-danger">{error}</p>}

        <div className="flex justify-center mt-4">
          <button
            className="button is-primary"
            onClick={handleResetClick}
            disabled={loading}
          >
            {loading ? "Redefinindo..." : "Redefinir Senha"}
          </button>
        </div>
      </div>

      {showConfirmModal && (
        <ModalScreen
          title="Confirmar Redefinição de Senha"
          onClose={() => setShowConfirmModal(false)}
          state="confirm"
          footer={
            <div className="flex justify-end gap-2 p-4">
              <button
                className="button is-white"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancelar
              </button>
              <button className="button is-primary" onClick={confirmReset}>
                <span className="icon is-small">
                  <Check className="inline mr-1" />
                </span>
                <span>Confirmar Redefinição</span>
              </button>
            </div>
          }
        >
          <div className="p-6">
            <div className="notification is-warning is-light mb-6 flex items-start">
              <span className="icon has-text-warning mr-3 mt-1">
                <i className="fas fa-exclamation-triangle"></i>
              </span>
              <div>
                <p className="font-bold text-gray-800">
                  Tem certeza que deseja redefinir a senha?
                </p>
                <p className="text-sm mt-1 text-gray-600">
                  Esta ação irá alterar a senha do utilizador{" "}
                  <strong>{user.username || user.email}</strong>. O utilizador
                  precisará usar a nova senha para fazer login.
                </p>
              </div>
            </div>
          </div>
        </ModalScreen>
      )}
    </>
  );
};

export default ResetPassword;
